import { useCallback, useEffect, useRef } from 'react';

import { useCanvasSelectionCircles, useCanvasUpdateHandler } from '.';
import { useApplianceContext } from '../../applianceContext';
import { useDrawingShapeContext } from '../../drawingShapeContext';
import { useSlabImgContext } from '../../slabImgContext';

import { drawingTool } from '~/core/engine/DrawingTool';
import { ColorModel, DrawingShape } from '~/core/models';
import { drawSideItems } from '~/core/utils';
import { isCanvasContextValid, isCanvasSafe } from '~/core/utils/canvasSafety';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';

export const useDrawingOperations = () => {
  const dispatch = useAppDispatch();

  // Track RAF ID to allow cleanup on unmount (prevents memory leaks)
  const rafIdRef = useRef<number | null>(null);

  const { isDraggingAppliance } = useApplianceContext();
  const slabImg = useSlabImgContext();

  const { updateShapesDimensions, updateShapesTexts, onDimensionEdit } =
    useCanvasUpdateHandler();
  const { renderSelectionCircles } = useCanvasSelectionCircles();

  const {
    getters: { activeDrawingShape },
  } = useDrawingShapeContext();

  const selectedColor = useAppSelector(selectSelectedColor);
  const canvas = useAppSelector(selectFabricCanvas);

  /**
   * Clears the canvas and updates shape dimensions
   * @param canvas - The fabric canvas
   * @param shapes - Optional shapes array to use (uses drawingShapes from closure if not provided)
   */
  const clearAndUpdateShapes = useCallback(
    (canvas: fabric.Canvas, shapes?: DrawingShape[]) => {
      if (!isCanvasSafe(canvas)) {
        return false;
      }

      drawingTool.clearCanvas({ canvas, preserveAppliances: true });
      updateShapesDimensions({ drawingShapes: shapes });

      return true;
    },
    [updateShapesDimensions]
  );

  /**
   *  Updates text elements and draws side items
   * @param overlayColor - The overlay color to use
   * @param shapes - The shapes array to use for updating texts and area
   */
  const updateTextsAndArea = useCallback(
    (overlayColor: string, shapes: DrawingShape[]) => {
      const renameSubscriptions = updateShapesTexts(overlayColor, shapes);

      drawSideItems({
        drawingShapes: shapes,
        selectedColor,
        canvas,
        activeDrawingShape,
        onDimensionEdit,
      });

      return { renameSubscriptions };
    },
    [
      updateShapesTexts,
      selectedColor,
      canvas,
      activeDrawingShape,
      onDimensionEdit,
    ]
  );

  /**
   * Fills the canvas with colors and renders selection circles
   */
  const fillCanvasAndRender = useCallback(
    (
      canvas: fabric.Canvas,
      selectedColor: ColorModel,
      drawingShapes: DrawingShape[]
    ) => {
      if (!isCanvasSafe(canvas)) {
        return false;
      }

      drawingTool.fillColor(
        canvas,
        selectedColor,
        drawingShapes,
        isDraggingAppliance,
        slabImg.element
      );

      renderSelectionCircles();

      return true;
    },
    [isDraggingAppliance, slabImg.element, renderSelectionCircles]
  );

  /**
   * Safely renders the canvas with additional safety checks.
   * Cancels any pending RAF to prevent stale renders and memory leaks.
   */
  const safeRenderCanvas = useCallback((canvas: fabric.Canvas) => {
    // Cancel any pending RAF to prevent multiple queued renders
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (isCanvasSafe(canvas)) {
        try {
          if (isCanvasContextValid(canvas)) {
            canvas.renderAll();
          }
        } catch (error) {
          console.warn('Error in canvas renderAll:', error);
        }
      }
    });
  }, []);

  /**
   * Completes the drawing operation and updates state
   */
  const finalizeDrawing = useCallback(() => {
    dispatch(updateNeedRedrawCanvas(false));
  }, [dispatch]);

  // Cleanup: Cancel any pending RAF on unmount to prevent memory leaks
  useEffect(
    () => () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    },
    []
  );

  return {
    clearAndUpdateShapes,
    updateTextsAndArea,
    fillCanvasAndRender,
    safeRenderCanvas,
    finalizeDrawing,
  };
};
